import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  api,
  compressImage,
  formatApiErrorDetail,
  getElectricalRoomMonitorings,
  getElectricalRoomReportUrl,
  downloadElectricalRoomReport,
} from "@/lib/api";
import {
  RISK_LABELS,
  RISK_COLORS,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  CHECK_STATUS_LABELS,
  formatDate,
} from "@/lib/labels";
import ElectricInspectionFlow from "@/components/ElectricInspectionFlow";
import RecordActions from "@/components/RecordActions";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import ReviewDialog from "@/components/ReviewDialog";
import MonitoringEditDialog from "@/components/MonitoringEditDialog";
import {
  RoomHistoryDialog,
  AiAnalysisDialog,
} from "@/components/ElectricRoomExtras";
import { EvidenceGallery } from "@/components/EvidenceGallery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Zap,
  Plus,
  Pencil,
  Trash2,
  Power,
  PlayCircle,
  History,
  MapPinned,
  Check,
  X,
  Minus,
  Sparkles,
  HeartPulse,
  Rat,
  FileDown,
  ClipboardList,
  CalendarDays,
  Save,
  GripVertical,
} from "lucide-react";

const HEALTH_CLS = {
  verde: "text-emerald-400",
  amarelo: "text-amber-400",
  laranja: "text-orange-400",
  vermelho: "text-red-400",
};

const MANAGE = [
  "admin",
  "supervisor",
  "gestor",
];

const INSPECT = [
  "admin",
  "supervisor",
  "tecnico",
  "gestor",
];

const RISK_CARD_BORDER = {
  verde: "border-emerald-500/50",
  amarelo: "border-amber-500/50",
  laranja: "border-orange-500/50",
  vermelho: "border-red-500/60",
  cinza: "border-border",
};

const ROOM_TYPE_LABELS = {
  sala_eletrica: "Sala Elétrica",
  cabine_remota: "Cabine Remota",
};

function RoomDialog({
  open,
  onOpenChange,
  initial,
  clients,
  onSaved,
}) {
  const [form, setForm] = useState({
    name: "",
    code: "",
    client_id: "",
    unit_id: "",
    area_id: "",
    description: "",
    room_type: "sala_eletrica",
  });

  const [units, setUnits] = useState([]);
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    if (!open) return;

    if (initial) {
      setForm({
        name: initial.name || "",
        code: initial.code || "",
        client_id: initial.client_id || "",
        unit_id: initial.unit_id || "",
        area_id: initial.area_id || "",
        description: initial.description || "",
        room_type:
          initial.room_type || "sala_eletrica",
      });
    } else {
      setForm({
        name: "",
        code: "",
        client_id: "",
        unit_id: "",
        area_id: "",
        description: "",
        room_type: "sala_eletrica",
      });
    }
  }, [open, initial]);

  useEffect(() => {
    if (form.client_id) {
      api
        .get("/units", {
          params: {
            client_id: form.client_id,
          },
        })
        .then(({ data }) => setUnits(data))
        .catch(() => setUnits([]));
    } else {
      setUnits([]);
    }
  }, [form.client_id]);

  useEffect(() => {
    if (form.unit_id) {
      api
        .get("/areas", {
          params: {
            unit_id: form.unit_id,
          },
        })
        .then(({ data }) => setAreas(data))
        .catch(() => setAreas([]));
    } else {
      setAreas([]);
    }
  }, [form.unit_id]);

  const set = (patch) =>
    setForm((current) => ({
      ...current,
      ...patch,
    }));

  const save = async () => {
    if (form.name.trim().length < 2) {
      return toast.error(
        "Informe o nome da sala."
      );
    }

    if (!form.client_id || !form.unit_id) {
      return toast.error(
        "Selecione cliente e unidade."
      );
    }

    const body = {
      ...form,
      area_id: form.area_id || null,
      room_type:
        form.room_type || "sala_eletrica",
    };

    try {
      if (initial?.id) {
        await api.put(
          `/electric-rooms/${initial.id}`,
          body
        );

        toast.success(
          "Ambiente atualizado."
        );
      } else {
        await api.post(
          "/electric-rooms",
          body
        );

        toast.success(
          form.room_type === "cabine_remota"
            ? "Cabine remota cadastrada."
            : "Sala elétrica cadastrada."
        );
      }

      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        )
      );
    }
  };

  const typeLabel =
    form.room_type === "cabine_remota"
      ? "Cabine Remota"
      : "Sala Elétrica";

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="max-w-lg"
        data-testid="room-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            {initial?.id
              ? `Editar ${typeLabel}`
              : form.room_type ===
                "cabine_remota"
              ? "Nova Cabine Remota"
              : "Nova Sala Elétrica"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Input
              className="col-span-2"
              value={form.name}
              onChange={(e) =>
                set({
                  name: e.target.value,
                })
              }
              placeholder="Nome da sala *"
              data-testid="room-name-input"
            />

            <Input
              value={form.code}
              onChange={(e) =>
                set({
                  code: e.target.value,
                })
              }
              placeholder="Código"
              data-testid="room-code-input"
            />
          </div>

          <Select
            value={form.room_type}
            onValueChange={(value) =>
              set({
                room_type: value,
              })
            }
          >
            <SelectTrigger
              data-testid="room-type-select"
            >
              <SelectValue placeholder="Tipo *" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="sala_eletrica">
                Sala Elétrica
              </SelectItem>

              <SelectItem value="cabine_remota">
                Cabine Remota
              </SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={form.client_id}
            onValueChange={(value) =>
              set({
                client_id: value,
                unit_id: "",
                area_id: "",
              })
            }
          >
            <SelectTrigger
              data-testid="room-client-select"
            >
              <SelectValue placeholder="Cliente *" />
            </SelectTrigger>

            <SelectContent>
              {clients.map((client) => (
                <SelectItem
                  key={client.id}
                  value={client.id}
                >
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={form.unit_id}
            onValueChange={(value) =>
              set({
                unit_id: value,
                area_id: "",
              })
            }
            disabled={!form.client_id}
          >
            <SelectTrigger
              data-testid="room-unit-select"
            >
              <SelectValue placeholder="Unidade *" />
            </SelectTrigger>

            <SelectContent>
              {units.map((unit) => (
                <SelectItem
                  key={unit.id}
                  value={unit.id}
                >
                  {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={form.area_id || "nenhuma"}
            onValueChange={(value) =>
              set({
                area_id:
                  value === "nenhuma"
                    ? ""
                    : value,
              })
            }
            disabled={!form.unit_id}
          >
            <SelectTrigger
              data-testid="room-area-select"
            >
              <SelectValue placeholder="Área (opcional)" />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="nenhuma">
                Sem área vinculada
              </SelectItem>

              {areas.map((area) => (
                <SelectItem
                  key={area.id}
                  value={area.id}
                >
                  {area.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) =>
              set({
                description: e.target.value,
              })
            }
            placeholder="Descrição / localização física"
            className="resize-none"
            data-testid="room-description-input"
          />

          <Button
            className="w-full"
            onClick={save}
            data-testid="room-save-btn"
          >
            {initial?.id
              ? "Salvar alterações"
              : "Cadastrar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CHECK_ICON = {
  conforme: Check,
  nao_conforme: X,
  nao_aplicavel: Minus,
};

const CHECK_CLS = {
  conforme: "text-emerald-400",
  nao_conforme: "text-red-400",
  nao_aplicavel: "text-slate-400",
};

function InspectionDetailDialog({
  inspId,
  onClose,
}) {
  const [insp, setInsp] = useState(null);

  useEffect(() => {
    if (!inspId) return;

    api
      .get(
        `/electric-rooms/inspections/${inspId}`
      )
      .then(({ data }) => setInsp(data))
      .catch(() => onClose());
  }, [inspId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog
      open={!!inspId}
      onOpenChange={(open) =>
        !open && onClose()
      }
    >
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin"
        data-testid="inspection-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">
            Detalhe da Inspeção
          </DialogTitle>
        </DialogHeader>

        {!insp ? (
          <div className="py-10 flex justify-center">
            <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="outline"
                className={
                  RISK_COLORS[insp.risk_level]
                }
              >
                {RISK_LABELS[insp.risk_level]}
              </Badge>

              <Badge variant="outline">
                {insp.nc_count} não conformidade(s)
              </Badge>

              <span className="text-xs text-muted-foreground">
                {insp.room_name} ·{" "}
                {insp.inspector_name} ·{" "}
                {formatDate(
                  insp.finished_at
                )}
              </span>
            </div>

            {insp.occurrence_protocols
              ?.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Ocorrências geradas:{" "}
                <span className="font-mono">
                  {insp.occurrence_protocols.join(
                    ", "
                  )}
                </span>
              </p>
            )}

            <div className="space-y-2">
              {(insp.checklist || []).map(
                (check, index) => {
                  const Icon =
                    CHECK_ICON[
                      check.status
                    ] || Minus;

                  return (
                    <div
                      key={index}
                      className="rounded-md border border-border p-3"
                    >
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-sm flex items-center gap-2">
                          <Icon
                            size={14}
                            className={
                              CHECK_CLS[
                                check.status
                              ]
                            }
                          />

                          {check.label}
                        </p>

                        <div className="flex items-center gap-2">
                          {check.status ===
                            "nao_conforme" && (
                            <Badge
                              variant="outline"
                              className={
                                PRIORITY_COLORS[
                                  check.severity
                                ]
                              }
                            >
                              {
                                PRIORITY_LABELS[
                                  check.severity
                                ]
                              }
                            </Badge>
                          )}

                          <span
                            className={`text-xs font-semibold ${
                              CHECK_CLS[
                                check.status
                              ]
                            }`}
                          >
                            {
                              CHECK_STATUS_LABELS[
                                check.status
                              ]
                            }
                          </span>
                        </div>
                      </div>

                      {check.notes && (
                        <p className="text-xs text-muted-foreground mt-1.5">
                          {check.notes}
                        </p>
                      )}

                      {check.photos?.length >
                        0 && (
                        <div className="mt-2">
                          <EvidenceGallery
                            photos={
                              check.photos
                            }
                            cols="grid-cols-4 sm:grid-cols-6"
                            testId={`insp-item-photos-${index}`}
                          />
                        </div>
                      )}
                    </div>
                  );
                }
              )}
            </div>

            {insp.droppings?.found && (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3"
                data-testid="insp-droppings"
              >
                <p className="text-sm font-medium flex items-center gap-2">
                  <Rat
                    size={14}
                    className="text-amber-400"
                  />

                  Fezes de roedor encontradas
                </p>

                <p className="text-xs text-muted-foreground mt-1">
                  Quantidade aproximada:{" "}
                  {insp.droppings.quantity}
                  {insp.droppings.location
                    ? ` · Local: ${insp.droppings.location}`
                    : ""}
                </p>

                {insp.droppings.notes && (
                  <p className="text-xs text-muted-foreground mt-1 italic">
                    "{insp.droppings.notes}"
                  </p>
                )}

                {insp.droppings.photos
                  ?.length > 0 && (
                  <div className="mt-2">
                    <EvidenceGallery
                      photos={
                        insp.droppings.photos
                      }
                      cols="grid-cols-4 sm:grid-cols-6"
                      testId="insp-droppings-photos"
                    />
                  </div>
                )}
              </div>
            )}

            {insp.traces?.length > 0 && (
              <div
                className="rounded-md border border-orange-500/40 bg-orange-500/5 p-3"
                data-testid="insp-traces"
              >
                <p className="text-sm font-medium">
                  Vestígios identificados
                </p>

                <div className="flex flex-wrap gap-1.5 mt-2">
                  {insp.traces.map(
                    (trace, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="bg-orange-500/10 text-orange-400 border-orange-500/30"
                      >
                        {trace.label}
                        {trace.detail
                          ? `: ${trace.detail}`
                          : ""}
                      </Badge>
                    )
                  )}
                </div>

                {insp.traces_notes && (
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    "{insp.traces_notes}"
                  </p>
                )}

                {insp.traces_photos
                  ?.length > 0 && (
                  <div className="mt-2">
                    <EvidenceGallery
                      photos={
                        insp.traces_photos
                      }
                      cols="grid-cols-4 sm:grid-cols-6"
                      testId="insp-traces-photos"
                    />
                  </div>
                )}
              </div>
            )}

            {insp.captures?.length > 0 && (
              <div
                className="rounded-md border border-rose-500/40 bg-rose-500/5 p-3 space-y-2"
                data-testid="insp-captures"
              >
                <p className="text-sm font-medium flex items-center gap-2">
                  <Rat
                    size={14}
                    className="text-rose-400"
                  />

                  Capturas de roedores (
                  {insp.rodents_total})
                </p>

                {insp.captures.map(
                  (capture, index) => (
                    <div
                      key={index}
                      className="text-xs text-muted-foreground"
                    >
                      <span className="font-medium text-foreground">
                        {capture.quantity}x{" "}
                        {capture.species_name}
                      </span>{" "}
                      ·{" "}
                      {capture.situation ===
                      "vivo"
                        ? "Vivo"
                        : "Morto"}
                      {capture.notes
                        ? ` — ${capture.notes}`
                        : ""}

                      {capture.photos?.length >
                        0 && (
                        <div className="mt-1.5">
                          <EvidenceGallery
                            photos={
                              capture.photos
                            }
                            cols="grid-cols-4 sm:grid-cols-6"
                            testId={`insp-capture-photos-${index}`}
                          />
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            )}

            {insp.general_photos?.length >
              0 && (
              <div>
                <p className="text-sm font-medium mb-2">
                  Fotos gerais da sala
                </p>

                <EvidenceGallery
                  photos={
                    insp.general_photos
                  }
                  cols="grid-cols-4 sm:grid-cols-6"
                  testId="insp-general-photos"
                />
              </div>
            )}

            {insp.notes && (
              <p className="text-sm text-muted-foreground italic">
                "{insp.notes}"
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
function RetroInspectionDialog({
  rooms,
  template,
  open,
  onOpenChange,
  onSaved,
}) {
  const { user } = useAuth();

  const activeRooms = (rooms || []).filter(
    (room) => room.active !== false
  );

  const [roomId, setRoomId] = useState("");
  const [inspDate, setInspDate] = useState("");
  const [inspTime, setInspTime] =
    useState("");
  const [notes, setNotes] = useState("");
  const [answers, setAnswers] = useState({});
  const [generalPhotos, setGeneralPhotos] =
    useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setRoomId(activeRooms[0]?.id || "");
      setInspDate("");
      setInspTime("");
      setNotes("");
      setAnswers({});
      setGeneralPhotos([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const checklist = template?.checklist || [];

  const addItemPhoto = async (key, files) => {
    const arr = [];
    for (const f of Array.from(files || [])) {
      arr.push(await compressImage(f));
    }
    setAnswers((current) => ({
      ...current,
      [key]: {
        ...(current[key] || {}),
        photos: [
          ...((current[key] || {}).photos || []),
          ...arr,
        ].slice(0, 10),
      },
    }));
  };

  const addGeneralPhoto = async (files) => {
    const arr = [];
    for (const f of Array.from(files || [])) {
      arr.push(await compressImage(f));
    }
    setGeneralPhotos((current) =>
      [...current, ...arr].slice(0, 10)
    );
  };

  const submit = async () => {
    if (!roomId) {
      return toast.error("Selecione a sala elétrica.");
    }
    if (!inspDate) {
      return toast.error("Informe a data da inspeção.");
    }

    const inspectedAt = inspTime
      ? `${inspDate}T${inspTime}`
      : `${inspDate}T12:00`;

    const built = [];
    const missingNc = [];

    for (const item of checklist) {
      const answer = answers[item.id || item.key] || {};
      const status = answer.status || "";

      if (item.required && !status) {
        missingNc.push(item.label);
        continue;
      }

      built.push({
        key: item.key,
        label: item.label,
        status: status || "nao_aplicavel",
        severity: answer.severity || item.severity || "media",
        notes: answer.notes || "",
        photos: answer.photos || [],
      });
    }

    if (missingNc.length) {
      return toast.error(
        `Preencha o status das perguntas obrigatórias antes de salvar.`
      );
    }

    // Evidência: o backend exige ao menos 1 foto (general_photos ou item NC).
    const totalPhotos =
      generalPhotos.length +
      built.reduce(
        (sum, item) =>
          sum + (item.photos?.length || 0),
        0
      );

    if (totalPhotos === 0) {
      return toast.error(
        "Anexe ao menos 1 foto de evidência da sala antes de salvar a inspeção retroativa."
      );
    }

    const missingNcPhotos = built.filter(
      (item) =>
        item.status === "nao_conforme" &&
        (item.photos?.length || 0) === 0
    );

    if (missingNcPhotos.length) {
      return toast.error(
        `Evidência fotográfica obrigatória para o item não conforme: ${missingNcPhotos[0].label}`
      );
    }

    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/electric-rooms/${roomId}/inspections`,
        {
          checklist: built,
          general_photos: generalPhotos,
          notes,
          rodent_found: false,
          captures: [],
          droppings: null,
          traces: [],
          traces_photos: [],
          traces_notes: "",
          gps: null,
          inspected_at: inspectedAt,
          is_retroactive: true,
        }
      );
      toast.success(
        "Inspeção retroativa registrada com sucesso."
      );
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        ) || "Não foi possível salvar a inspeção retroativa."
      );
    } finally {
      setSubmitting(false);
    }
  };

return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin"
        data-testid="retro-inspection-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <CalendarDays size={17} /> Inspeção retroativa
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-xs text-muted-foreground">
            Disponibilizada somente para Supervisores na interface WEB. A data
            informada representa o momento REAL da inspeção; o momento do
            lançamento no sistema também é preservado.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5 md:col-span-3">
              <Label className="text-xs">Sala elétrica</Label>
              <Select value={roomId} onValueChange={setRoomId}>
                <SelectTrigger data-testid="retro-room-select">
                  <SelectValue placeholder="Selecione a sala" />
                </SelectTrigger>
                <SelectContent>
                  {activeRooms.map((room) => (
                    <SelectItem key={room.id} value={room.id}>
                      {room.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Data da inspeção</Label>
              <Input
                type="date"
                value={inspDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setInspDate(e.target.value)}
                data-testid="retro-date-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Hora</Label>
              <Input
                type="time"
                value={inspTime}
                onChange={(e) => setInspTime(e.target.value)}
                data-testid="retro-time-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Responsável</Label>
              <Input
                value={user?.name || ""}
                readOnly
                className="bg-muted/40"
              />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-semibold">
              Checklist ({checklist.length} perguntas)
            </p>
            <div className="space-y-2">
              {checklist.map((item) => {
                const answer =
                  answers[item.id || item.key] || {};
                return (
                  <div
                    key={item.id || item.key}
                    className="rounded-lg border border-border p-3 space-y-2"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-medium">
                        {item.label}
                      </p>
                      <div className="flex items-center gap-1.5">
                        {["conforme", "nao_conforme", "nao_aplicavel"].map((status) => (
                          <button
                            key={status}
                            type="button"
                            onClick={() =>
                              setAnswers((current) => ({
                                ...current,
                                [item.id || item.key]: {
                                  ...(current[item.id || item.key] || {}),
                                  status,
                                },
                              }))
                            }
                            className={`px-2 py-1 rounded-md text-[10px] border ${
                              answer.status === status
                                ? status === "conforme"
                                  ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/40"
                                  : status === "nao_conforme"
                                  ? "bg-red-500/15 text-red-500 border-red-500/40"
                                  : "bg-muted text-muted-foreground border-border"
                                : "border-border text-muted-foreground hover:bg-accent"
                            }`}
                            data-testid={`retro-item-${item.key}-${status}`}
                          >
                            {status === "conforme"
                              ? "Conforme"
                              : status === "nao_conforme"
                              ? "Não conforme"
                              : "N/A"}
                          </button>
                        ))}
                      </div>
                    </div>

                    {answer.status === "nao_conforme" && (
                      <div className="space-y-1.5">
                        <Label className="text-[10px]">Observação da NC</Label>
                        <Textarea
                          rows={2}
                          value={answer.notes || ""}
                          onChange={(e) =>
                            setAnswers((current) => ({
                              ...current,
                              [item.id || item.key]: {
                                ...(current[item.id || item.key] || {}),
                                notes: e.target.value,
                              },
                            }))
                          }
                          placeholder="Descreva a não conformidade"
                        />

                        <Label className="text-[10px]">
                          Foto da NC ({(answer.photos || []).length})
                        </Label>
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          className="file:text-xs text-xs"
                          onChange={(e) =>
                            addItemPhoto(
                              item.id || item.key,
                              e.target.files
                            )
                          }
                          data-testid={`retro-item-photo-${item.key}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Observações</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observações gerais da inspeção"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">
              Evidência da sala ({generalPhotos.length})
            </Label>
            <input
              type="file"
              accept="image/*"
              multiple
              className="file:text-xs text-xs"
              onChange={(e) =>
                addGeneralPhoto(e.target.files)
              }
              data-testid="retro-general-photos"
            />
            {generalPhotos.length === 0 && (
              <p className="text-[10px] text-muted-foreground">
                O backend exige ao menos 1 foto de evidência da sala.
              </p>
            )}
          </div>

          <Button
            className="w-full"
            onClick={submit}
            disabled={submitting}
            data-testid="retro-submit-btn"
          >
            {submitting
              ? "Salvando..."
              : "Salvar inspeção retroativa"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
function ChecklistConfigDialog({
  open,
  onOpenChange,
  onSaved,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] =
    useState(false);
  const [saving, setSaving] =
    useState(false);
  const [editing, setEditing] =
    useState(null);
  const [showForm, setShowForm] =
    useState(false);
  const [confirmDelete, setConfirmDelete] =
    useState(null);

  const [form, setForm] = useState({
    label: "",
    key: "",
    type: "sim_nao",
    required: true,
    photo_required: false,
    severity: "media",
    active: true,
  });

  const slugify = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        "/electric-rooms/checklist-items",
        { params: { include_inactive: true } }
      );
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        ) || "Não foi possível carregar o checklist."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      load();
      setEditing(null);
      setShowForm(false);
      setConfirmDelete(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const resetForm = () => {
    setForm({
      label: "",
      key: "",
      type: "sim_nao",
      required: true,
      photo_required: false,
      severity: "media",
      active: true,
    });
    setEditing(null);
    setShowForm(true);
  };

  const saveItem = async () => {
    if (!form.label?.trim()) {
      return toast.error(
        "Informe o texto da pergunta."
      );
    }

    const payload = {
      label: form.label.trim(),
      key:
        form.key?.trim() ||
        slugify(form.label),
      answer_type: form.type,
      required: form.required,
      photo_required: form.photo_required,
      severity: form.severity,
      active: form.active,
    };

    setSaving(true);
    try {
      if (editing?.id) {
        await api.put(
          `/electric-rooms/checklist-items/${editing.id}`,
          payload
        );
        toast.success(
          "Pergunta atualizada."
        );
      } else {
        await api.post(
          `/electric-rooms/checklist-items`,
          payload
        );
        toast.success(
          "Pergunta adicionada."
        );
      }
      setShowForm(false);
      setEditing(null);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        ) || "Não foi possível salvar a pergunta."
      );
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async () => {
    if (!confirmDelete?.id) return;
    setSaving(true);
    try {
      await api.delete(
        `/electric-rooms/checklist-items/${confirmDelete.id}`
      );
      toast.success(
        "Pergunta removida."
      );
      setConfirmDelete(null);
      await load();
      onSaved?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        ) || "Não foi possível remover a pergunta."
      );
    } finally {
      setSaving(false);
    }
  };
return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin"
        data-testid="checklist-config-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ClipboardList size={17} /> Configuração do checklist
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Carregando checklist...
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                {items.length} pergunta(s) ativa(s) para o checklist atual
              </p>

              {!showForm && (
                <Button
                  size="sm"
                  onClick={resetForm}
                  data-testid="checklist-add-btn"
                >
                  <Plus size={14} className="mr-1.5" />
                  Adicionar pergunta
                </Button>
              )}
            </div>

            {showForm && (
              <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold">
                    {editing?.id
                      ? "Editar pergunta"
                      : "Nova pergunta"}
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setEditing(null);
                    }}
                    className="text-muted-foreground hover:text-foreground"
                    aria-label="Fechar formulário"
                  >
                    ✕
                  </button>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs">Pergunta</Label>
                  <Input
                    value={form.label}
                    onChange={(e) =>
                      setForm((current) => ({
                        ...current,
                        label: e.target.value,
                      }))
                    }
                    placeholder="Ex.: Disjuntor em condições adequadas?"
                    data-testid="checklist-question-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Tipo de resposta</Label>
                    <Select
                      value={form.type}
                      onValueChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          type: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sim_nao">Sim/Não</SelectItem>
                        <SelectItem value="sim_nao_na">
                          Sim/Não/Não se aplica
                        </SelectItem>
                        <SelectItem value="texto">Texto</SelectItem>
                        <SelectItem value="numero">Número</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs">Severidade padrão</Label>
                    <Select
                      value={form.severity}
                      onValueChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          severity: value,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="baixa">Baixa</SelectItem>
                        <SelectItem value="media">Média</SelectItem>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="critica">Crítica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={form.required}
                      onChange={(e) =>
                        setForm((current) => ({
                          ...current,
                          required: e.target.checked,
                        }))
                      }
                      className="h-3.5 w-3.5"
                    />
                    Obrigatória
                  </label>

                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={form.photo_required}
                      onChange={(e) =>
                        setForm((current) => ({
                          ...current,
                          photo_required: e.target.checked,
                        }))
                      }
                      className="h-3.5 w-3.5"
                    />
                    Foto obrigatória
                  </label>

                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={form.active}
                      onChange={(e) =>
                        setForm((current) => ({
                          ...current,
                          active: e.target.checked,
                        }))
                      }
                      className="h-3.5 w-3.5"
                    />
                    Ativa
                  </label>
                </div>

                <Button
                  size="sm"
                  className="w-full"
                  onClick={saveItem}
                  disabled={saving}
                  data-testid="checklist-save-btn"
                >
                  <Save size={14} className="mr-1.5" />
                  {saving
                    ? "Salvando..."
                    : editing?.id
                    ? "Salvar alterações"
                    : "Adicionar pergunta"}
                </Button>
              </div>
            )}

            <div className="space-y-2">
              {items.map((item, index) => (
                <div
                key={item.id || item.key}
                className="rounded-lg border border-border p-3"
                data-testid={`checklist-item-${item.key || item.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <GripVertical
                      size={14}
                      className="mt-0.5 shrink-0 text-muted-foreground/50"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-medium break-words">
                        {index + 1}. {item.label}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
                        <span>{item.answer_type || "sim_nao"}</span>
                        {item.required && <span>· obrigatória</span>}
                        {!item.active && (
                          <span className="text-amber-500">
                            · inativa
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => {
                        const checkbox =
                          items[index];
                        setForm({
                          label: checkbox.label,
                          key: checkbox.key,
                          type:
                            checkbox.answer_type ||
                            "sim_nao",
                          required:
                            checkbox.required !==
                            false,
                          photo_required:
                            checkbox.photo_required ===
                            true,
                          severity:
                            checkbox.severity ||
                            "media",
                          active:
                            checkbox.active !==
                            false,
                        });
                        setEditing(checkbox);
                        setShowForm(true);
                      }}
                      aria-label="Editar pergunta"
                    >
                      <Pencil size={14} />
                    </Button>

                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-red-500 hover:text-red-500"
                      onClick={() =>
                        setConfirmDelete(item)
                      }
                      aria-label="Remover pergunta"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </div>
              ))}

              {items.length === 0 && (
                <p className="p-6 text-center text-muted-foreground text-sm">
                  Nenhuma pergunta no checklist.
                </p>
              )}
            </div>
          </div>
        )}

        {confirmDelete && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 space-y-3">
          <p className="text-xs">
            Remover a pergunta{" "}
            <strong>{confirmDelete.label}</strong>?
          </p>

          <p className="text-[11px] text-muted-foreground">
            Perguntas já respondidas em inspeções antigas continuam
            preservadas no histórico. A pergunta deixa de aparecer apenas em
            novas inspeções.
          </p>

          <div className="flex items-center gap-2 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                setConfirmDelete(null)
              }
            >
              Cancelar
            </Button>

            <Button
              size="sm"
              variant="destructive"
              onClick={removeItem}
              disabled={saving}
              data-testid="checklist-remove-confirm"
            >
              {saving
                ? "Removendo..."
                : "Remover"}
            </Button>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ElectricRooms() {
  const { user } = useAuth();

  const canManage = MANAGE.includes(
    user.role
  );

  const canInspect = INSPECT.includes(
    user.role
  );

  // Inspeção retroativa: SOMENTE Supervisor (WEB). O Admin mantém capacidades
  // administrativas normais. O mobile não possui este fluxo.
  const canRetroactive =
    String(user?.role || "")
      .trim()
      .toLowerCase() === "supervisor";

  const canChecklist = MANAGE.includes(user.role);

  const [clients, setClients] = useState([]);
  const [units, setUnits] = useState([]);

  const [clientId, setClientId] =
    useState("todos");

  const [unitId, setUnitId] =
    useState("todas");

  const [roomType, setRoomType] =
    useState("sala_eletrica");

  const [period, setPeriod] =
    useState("30d");

  const [historyPage, setHistoryPage] =
    useState(1);

  const [historyPages, setHistoryPages] =
    useState(0);

  const [historyTotal, setHistoryTotal] =
    useState(0);

  const [startDate, setStartDate] =
    useState("");

  const [endDate, setEndDate] =
    useState("");

  const [historyRoomId, setHistoryRoomId] =
    useState("");

  const [filterYear, setFilterYear] =
    useState(
      String(new Date().getFullYear())
    );

  const [filterMonth, setFilterMonth] =
    useState(
      String(new Date().getMonth() + 1)
    );

  const [filterWeek, setFilterWeek] =
    useState("");

  const [dash, setDash] =
    useState(null);

  const [allRooms, setAllRooms] =
    useState([]);

  const [inspections, setInspections] =
    useState([]);

  const [template, setTemplate] =
    useState(null);

  const [dialogOpen, setDialogOpen] =
    useState(false);

  const [editing, setEditing] =
    useState(null);

  const [flow, setFlow] =
    useState(null);

  const [detailId, setDetailId] =
    useState(null);

  const [aiOpen, setAiOpen] =
    useState(false);

  const [retroOpen, setRetroOpen] =
    useState(false);

  const [checklistOpen, setChecklistOpen] =
    useState(false);

  const [inspEdit, setInspEdit] =
    useState(null);

  const [inspReview, setInspReview] =
    useState(null);

  const [inspDelete, setInspDelete] =
    useState(null);

  const [loadingData, setLoadingData] =
    useState(false);

  const params = () => {
    const result = {};

    if (clientId !== "todos") {
      result.client_id = clientId;
    }

    if (unitId !== "todas") {
      result.unit_id = unitId;
    }

    result.room_type = roomType;

    return result;
  };

  const historyParams = () => {
    const result = params();

    result.page = historyPage;

    if (historyRoomId) {
      result.room_id = historyRoomId;
    }

    if (period === "personalizado") {
      if (startDate) {
        result.start_date = startDate;
      }
      if (endDate) {
        result.end_date = endDate;
      }
    } else if (period === "todos") {
      // Sem filtro de data: consulta todo o histórico (paginado).
    } else if (period === "semana") {
      result.period = "semana";
      result.year = Number(
        filterYear
      );

      if (filterWeek) {
        result.week = Number(
          filterWeek
        );
      }
    } else if (period === "mes") {
      result.period = "mes";
      result.year = Number(
        filterYear
      );
      result.month = Number(
        filterMonth
      );
    } else if (period === "ano") {
      result.period = "ano";
      result.year = Number(
        filterYear
      );
    } else if (period === "30d") {
      result.days = 30;
    } else if (period === "hoje") {
      result.period = "hoje";
    } else if (period === "7d") {
      result.period = "7d";
    } else if (period === "90d") {
      result.period = "90d";
    }

    return result;
  };

  const normalizeDashboard = (
    data
  ) => {
    const roomsData =
      data?.salas || {};

    const evidences =
      data?.evidencias_30d || {};

    const counts = {
      verde:
        roomsData.baixo_risco || 0,

      amarelo:
        roomsData.atencao || 0,

      laranja:
        roomsData.alto_risco || 0,

      vermelho:
        roomsData.critico || 0,

      cinza:
        roomsData.sem_inspecao || 0,
    };

    return {
      ...data,

      total:
        roomsData.total || 0,

      inspected_30d:
        roomsData.inspecionadas_30d || 0,

      counts,

      bio_30d: {
        roedores:
          evidences.roedores || 0,

        fezes:
          evidences.fezes || 0,

        vestigios:
          evidences.vestigios || 0,
      },

      rooms:
        data?.salas_detalhe || [],

      recent_ncs:
        data?.recent_ncs || [],
    };
  };

  const loadDash = async () => {
    const { data } =
      await api.get(
        "/electrical-rooms/dashboard",
        {
          params: params(),
        }
      );

    setDash(
      normalizeDashboard(data)
    );
  };

  const loadRooms = async () => {
    const { data } =
      await api.get(
        "/electric-rooms",
        {
          params: {
            ...params(),
            include_inactive: true,
          },
        }
      );

    setAllRooms(
      Array.isArray(data)
        ? data
        : data?.items || []
    );
  };

  const loadInspections =
    async () => {
      const data =
        await getElectricalRoomMonitorings(
          historyParams()
        );

      setInspections(
        data?.items || []
      );

      setHistoryTotal(
        Number(data?.total || 0)
      );

      setHistoryPages(
        Number(data?.pages || 0)
      );

      setHistoryPage(
        Number(data?.page || 1)
      );
    };

  const loadAll = async () => {
    setLoadingData(true);

    try {
      await Promise.all([
        loadDash(),
        loadRooms(),
        loadInspections(),
      ]);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        )
      );

      setDash(
        (current) =>
          current || {
            total: 0,
            inspected_30d: 0,
            counts: {},
            bio_30d: {},
            rooms: [],
            recent_ncs: [],
          }
      );
    } finally {
      setLoadingData(false);
    }
  };

  const downloadReport =
    async () => {
      try {
        const filters = {
          ...historyParams(),
          room_type: roomType,
        };

        delete filters.days;

        await downloadElectricalRoomReport(
          filters,
          roomType ===
            "cabine_remota"
            ? "relatorio_cabines_remotas.pdf"
            : "relatorio_salas_eletricas.pdf"
        );

        toast.success(
          roomType ===
            "cabine_remota"
            ? "PDF das cabines remotas gerado."
            : "PDF das salas elétricas gerado."
        );
      } catch (err) {
        try {
          const filters = {
            ...historyParams(),
            room_type: roomType,
          };

          delete filters.days;

          window.open(
            getElectricalRoomReportUrl(
              filters
            ),
            "_blank",
            "noopener,noreferrer"
          );
        } catch {
          toast.error(
            formatApiErrorDetail(
              err.response?.data?.detail
            )
          );
        }
      }
    };

  useEffect(() => {
    api
      .get("/clients")
      .then(({ data }) =>
        setClients(data)
      )
      .catch(() => {});

    api
      .get(
        "/electric-rooms/checklist-template"
      )
      .then(({ data }) =>
        setTemplate(data)
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (clientId !== "todos") {
      api
        .get("/units", {
          params: {
            client_id: clientId,
          },
        })
        .then(({ data }) =>
          setUnits(data)
        )
        .catch(() =>
          setUnits([])
        );
    } else {
      setUnits([]);
    }

    setUnitId("todas");
  }, [clientId]);

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientId,
    unitId,
    roomType,
    period,
    filterYear,
    filterMonth,
    filterWeek,
    historyPage,
    startDate,
    endDate,
    historyRoomId,
  ]);

  useEffect(() => {
    setHistoryPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, startDate, endDate, historyRoomId, clientId, unitId, roomType]);

  const startInspection = (
    startRoomId = null
  ) => {
    const active =
      (dash?.rooms || []).filter(
        (room) =>
          room.active !== false
      );

    if (!active.length) {
      return toast.error(
        `Nenhum ${
          roomType ===
          "cabine_remota"
            ? "cabine remota"
            : "ambiente"
        } ativo para inspecionar nos filtros atuais.`
      );
    }

    if (!template) {
      return toast.error(
        "Checklist não carregado."
      );
    }

    const startIndex =
      startRoomId
        ? Math.max(
            0,
            active.findIndex(
              (room) =>
                room.id ===
                startRoomId
            )
          )
        : 0;

    setFlow({
      rooms: active,
      startIndex,
    });
  };

  const toggleRoom = async (
    room
  ) => {
    try {
      await api.patch(
        `/electric-rooms/${room.id}/toggle`
      );

      toast.success(
        room.active === false
          ? `${
              ROOM_TYPE_LABELS[
                room.room_type ||
                  roomType
              ]
            } ativada.`
          : `${
              ROOM_TYPE_LABELS[
                room.room_type ||
                  roomType
              ]
            } desativada. Histórico preservado.`
      );

      loadAll();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(
          err.response?.data?.detail
        )
      );
    }
  };

  const deleteRoom = async (
    room
  ) => {
    if (
      !window.confirm(
        `Excluir ${
          room.room_type ===
          "cabine_remota"
            ? "a cabine remota"
            : "a sala"
        } "${room.name}"?`
      )
    ) {
      return;
    }

    try {
      await api.delete(
        `/electric-rooms/${room.id}`
      );

      toast.success(
        "Registro excluído."
      );

      loadAll();
    } catch (err) {
      const detail =
        formatApiErrorDetail(
          err.response?.data?.detail
        );

      if (
        String(detail).includes(
          "cascata"
        ) &&
        window.confirm(
          `${detail}\n\nConfirma excluir o ambiente E todos os monitoramentos dele?`
        )
      ) {
        try {
          await api.delete(
            `/electric-rooms/${room.id}?cascade=true`
          );

          toast.success(
            "Ambiente e monitoramentos excluídos."
          );

          loadAll();
          return;
        } catch (error) {
          return toast.error(
            formatApiErrorDetail(
              error.response?.data
                ?.detail
            )
          );
        }
      }

      toast.error(detail);
    }
  };

  if (flow) {
    return (
      <ElectricInspectionFlow
        rooms={flow.rooms}
        startIndex={
          flow.startIndex
        }
        template={template}
        onExit={() => {
          setFlow(null);
          loadAll();
        }}
        onFinished={() => {
          setFlow(null);
          loadAll();
        }}
      />
    );
  }

  if (!dash) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const counts =
    dash.counts || {};

  const typeLabel =
    roomType === "cabine_remota"
      ? "Cabines Remotas"
      : "Salas Elétricas";

  return (
    <div
      className="space-y-6"
      data-testid="electric-rooms-page"
    >
      {loadingData && (
        <div className="fixed top-3 right-3 z-50 rounded-full border border-border bg-card/95 px-3 py-1.5 text-xs text-muted-foreground shadow-lg">
          Atualizando...
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Zap
              size={26}
              className="text-amber-400"
            />

            {typeLabel}
          </h1>

          <p className="text-sm text-muted-foreground mt-1">
            Monitoramento sala a sala com
            checklist de TRX, placas de cola,
            canaletas e mapa de risco.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={clientId}
            onValueChange={
              setClientId
            }
          >
            <SelectTrigger
              className="w-44 h-9"
              data-testid="er-client-filter"
            >
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="todos">
                Todos os clientes
              </SelectItem>

              {clients.map((client) => (
                <SelectItem
                  key={client.id}
                  value={client.id}
                >
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={unitId}
            onValueChange={setUnitId}
            disabled={
              clientId === "todos"
            }
          >
            <SelectTrigger
              className="w-44 h-9"
              data-testid="er-unit-filter"
            >
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="todas">
                Todas as unidades
              </SelectItem>

              {units.map((unit) => (
                <SelectItem
                  key={unit.id}
                  value={unit.id}
                >
                  {unit.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={roomType}
            onValueChange={
              setRoomType
            }
          >
            <SelectTrigger
              className="w-48 h-9"
              data-testid="er-type-filter"
            >
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="sala_eletrica">
                Salas Elétricas
              </SelectItem>

              <SelectItem value="cabine_remota">
                Cabines Remotas
              </SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={period}
            onValueChange={setPeriod}
          >
            <SelectTrigger
              className="w-36 h-9"
              data-testid="er-period-filter"
            >
              <SelectValue />
            </SelectTrigger>

            <SelectContent>
              <SelectItem value="todos">
                Todo o histórico
              </SelectItem>

              <SelectItem value="hoje">
                Hoje
              </SelectItem>

              <SelectItem value="7d">
                7 dias
              </SelectItem>

              <SelectItem value="30d">
                30 dias
              </SelectItem>

              <SelectItem value="90d">
                90 dias
              </SelectItem>

              <SelectItem value="ano">
                Este ano
              </SelectItem>

              <SelectItem value="semana">
                Semana
              </SelectItem>

              <SelectItem value="mes">
                Mês
              </SelectItem>

              <SelectItem value="personalizado">
                Personalizado
              </SelectItem>
            </SelectContent>
          </Select>

          {(period === "semana" ||
            period === "mes" ||
            period === "ano") && (
            <Select
              value={filterYear}
              onValueChange={
                setFilterYear
              }
            >
              <SelectTrigger
                className="w-28 h-9"
                data-testid="er-year-filter"
              >
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {Array.from(
                  {
                    length: 6,
                  },
                  (_, index) =>
                    new Date().getFullYear() -
                    index
                ).map((year) => (
                  <SelectItem
                    key={year}
                    value={String(year)}
                  >
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {period === "mes" && (
            <Select
              value={filterMonth}
              onValueChange={
                setFilterMonth
              }
            >
              <SelectTrigger
                className="w-32 h-9"
                data-testid="er-month-filter"
              >
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {[
                  ["1", "Janeiro"],
                  ["2", "Fevereiro"],
                  ["3", "Março"],
                  ["4", "Abril"],
                  ["5", "Maio"],
                  ["6", "Junho"],
                  ["7", "Julho"],
                  ["8", "Agosto"],
                  ["9", "Setembro"],
                  ["10", "Outubro"],
                  ["11", "Novembro"],
                  ["12", "Dezembro"],
                ].map(
                  ([value, label]) => (
                    <SelectItem
                      key={value}
                      value={value}
                    >
                      {label}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          )}

          {period === "semana" && (
            <Input
              type="number"
              min="1"
              max="53"
              value={filterWeek}
              onChange={(event) =>
                setFilterWeek(
                  event.target.value
                )
              }
              placeholder="Semana"
              className="w-24 h-9"
              data-testid="er-week-filter"
            />
          )}

          {period === "personalizado" && (
            <>
              <Input
                type="date"
                value={startDate}
                onChange={(event) =>
                  setStartDate(
                    event.target.value
                  )
                }
                className="w-40 h-9"
                data-testid="er-start-date-filter"
                aria-label="Data inicial"
              />

              <Input
                type="date"
                value={endDate}
                onChange={(event) =>
                  setEndDate(
                    event.target.value
                  )
                }
                className="w-40 h-9"
                data-testid="er-end-date-filter"
                aria-label="Data final"
              />
            </>
          )}

          {(dash?.rooms || []).length > 0 && (
            <Select
              value={historyRoomId}
              onValueChange={(value) =>
                setHistoryRoomId(
                  value === "todas_salas"
                    ? ""
                    : value
                )
              }
            >
              <SelectTrigger
                className="w-52 h-9"
                data-testid="er-history-room-filter"
              >
                <SelectValue placeholder="Todas as salas" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="todas_salas">
                  Todas as salas
                </SelectItem>

                {(dash?.rooms || []).map(
                  (room) => (
                    <SelectItem
                      key={room.id}
                      value={room.id}
                    >
                      {room.name}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          )}

          {canInspect && (
            <Button
              variant="outline"
              onClick={downloadReport}
              data-testid="er-pdf-btn"
            >
              <FileDown
                size={15}
                className="mr-2"
              />

              PDF{" "}
              {roomType ===
              "cabine_remota"
                ? "Cabines"
                : "Salas"}
            </Button>
          )}

          {canInspect && (
            <Button
              variant="outline"
              onClick={() =>
                setAiOpen(true)
              }
              data-testid="er-ai-analysis-btn"
            >
              <Sparkles
                size={15}
                className="mr-2 text-primary"
              />

              Análise IA
            </Button>
          )}

          {canInspect && (
            <Button
              onClick={() =>
                startInspection()
              }
              data-testid="start-inspection-btn"
            >
              <PlayCircle
                size={15}
                className="mr-2"
              />

              Iniciar Inspeção
            </Button>
          )}

          {canRetroactive && (
            <Button
              variant="secondary"
              onClick={() =>
                setRetroOpen(true)
              }
              data-testid="retro-inspection-btn"
            >
              <History
                size={15}
                className="mr-2"
              />

              Inspeção retroativa
            </Button>
          )}

          {canChecklist && (
            <Button
              variant="outline"
              onClick={() =>
                setChecklistOpen(true)
              }
              data-testid="checklist-config-btn"
            >
              <ClipboardList
                size={15}
                className="mr-2"
              />

              Configurar checklist
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
        {[
          [
            "verde",
            counts.verde,
          ],
          [
            "amarelo",
            counts.amarelo,
          ],
          [
            "laranja",
            counts.laranja,
          ],
          [
            "vermelho",
            counts.vermelho,
          ],
          [
            "cinza",
            counts.cinza,
          ],
        ].map(([key, value]) => (
          <Card
            key={key}
            className={`p-3 border bg-card ${
              RISK_CARD_BORDER[key]
            }`}
            data-testid={`er-count-${key}`}
          >
            <p className="font-heading text-2xl font-bold">
              {value || 0}
            </p>

            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
              {RISK_LABELS[key]}
            </p>
          </Card>
        ))}

        <Card
          className="p-3 border-border bg-card"
          data-testid="er-count-inspected"
        >
          <p className="font-heading text-2xl font-bold">
            {dash.inspected_30d ||
              0}

            <span className="text-sm text-muted-foreground font-normal">
              /{dash.total || 0}
            </span>
          </p>

          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
            Inspecionadas (30d)
          </p>
        </Card>

        <Card
          className="p-3 border-border bg-card"
          data-testid="er-count-bio"
          title={`Roedores: ${
            dash.bio_30d
              ?.roedores || 0
          } · Fezes: ${
            dash.bio_30d?.fezes ||
            0
          } · Vestígios: ${
            dash.bio_30d
              ?.vestigios || 0
          }`}
        >
          <p className="font-heading text-2xl font-bold text-rose-400">
            {(dash.bio_30d
              ?.roedores || 0) +
              (dash.bio_30d
                ?.fezes || 0) +
              (dash.bio_30d
                ?.vestigios || 0)}
          </p>

          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
            Evidências Bio (30d)
          </p>
        </Card>
      </div>

      <Tabs defaultValue="mapa">
        <TabsList data-testid="er-tabs">
          <TabsTrigger
            value="mapa"
            data-testid="er-tab-mapa"
          >
            <MapPinned
              size={14}
              className="mr-1.5"
            />

            Mapa de Risco
          </TabsTrigger>

          <TabsTrigger
            value="salas"
            data-testid="er-tab-salas"
          >
            <Zap
              size={14}
              className="mr-1.5"
            />

            {typeLabel}
          </TabsTrigger>

          <TabsTrigger
            value="historico"
            data-testid="er-tab-historico"
          >
            <History
              size={14}
              className="mr-1.5"
            />

            Histórico
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="mapa"
          className="mt-4 space-y-5"
        >
          {dash.rooms.length === 0 ? (
            <Card className="p-10 border-border bg-card text-center">
              <Zap
                size={32}
                className="mx-auto text-muted-foreground"
              />

              <p className="text-sm text-muted-foreground mt-3">
                Nenhum ambiente cadastrado
                nos filtros atuais.
              </p>

              {canManage && (
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                  data-testid="er-empty-add-btn"
                >
                  <Plus
                    size={15}
                    className="mr-2"
                  />

                  Cadastrar primeiro ambiente
                </Button>
              )}
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {dash.rooms.map(
                (room) => (
                  <Card
                    key={room.id}
                    className={`p-4 border-2 bg-card ${
                      RISK_CARD_BORDER[
                        room.risk_level
                      ]
                    }`}
                    data-testid={`room-card-${room.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-heading font-semibold text-sm truncate">
                          {room.name}

                          {room.code
                            ? ` · ${room.code}`
                            : ""}
                        </p>

                        <p className="text-[9px] uppercase tracking-wider text-primary/80 mt-0.5">
                          {room.room_type ===
                          "cabine_remota"
                            ? "Cabine Remota"
                            : "Sala Elétrica"}
                        </p>

                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                          {
                            room.client_name
                          }{" "}
                          ›{" "}
                          {
                            room.unit_name
                          }
                        </p>
                      </div>

                      <Badge
                        variant="outline"
                        className={
                          RISK_COLORS[
                            room.risk_level
                          ]
                        }
                        data-testid={`room-risk-${room.id}`}
                      >
                        {
                          RISK_LABELS[
                            room.risk_level
                          ]
                        }
                      </Badge>
                    </div>

                    <div className="flex items-center justify-between gap-2 mt-3">
                      <div className="text-xs text-muted-foreground space-y-0.5">
                        {room.last_inspection ? (
                          <>
                            <p>
                              Última inspeção:{" "}
                              {formatDate(
                                room
                                  .last_inspection
                                  .finished_at
                              )}
                            </p>

                            <p>
                              {
                                room
                                  .last_inspection
                                  .nc_count
                              }{" "}
                              NC · por{" "}
                              {
                                room
                                  .last_inspection
                                  .inspector_name
                              }

                              {room
                                .last_inspection
                                .rodents_total >
                                0 && (
                                <span className="text-rose-400 font-medium">
                                  {" "}
                                  ·{" "}
                                  <Rat
                                    size={11}
                                    className="inline -mt-0.5"
                                  />{" "}
                                  {
                                    room
                                      .last_inspection
                                      .rodents_total
                                  }{" "}
                                  roedor(es)
                                </span>
                              )}
                            </p>
                          </>
                        ) : (
                          <p>
                            Nunca inspecionada
                          </p>
                        )}
                      </div>

                      <div
                        className="text-center shrink-0"
                        title="Índice de saúde do ambiente (0-100)"
                        data-testid={`room-health-${room.id}`}
                      >
                        <p
                          className={`font-heading text-xl font-bold leading-none ${
                            HEALTH_CLS[
                              room
                                .health
                                ?.band
                            ] ||
                            "text-muted-foreground"
                          }`}
                        >
                          {room.health
                            ?.score ??
                            "—"}
                        </p>

                        <p className="text-[8px] uppercase tracking-wider text-muted-foreground flex items-center gap-0.5 justify-center mt-0.5">
                          <HeartPulse
                            size={9}
                          />

                          Saúde
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-2 mt-3">
                      {canInspect && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() =>
                            startInspection(
                              room.id
                            )
                          }
                          data-testid={`room-inspect-btn-${room.id}`}
                        >
                          <PlayCircle
                            size={13}
                            className="mr-1.5"
                          />

                          Inspecionar
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() =>
                          setHistoryRoomId(
                            room.id
                          )
                        }
                        data-testid={`room-history-btn-${room.id}`}
                      >
                        <History
                          size={13}
                          className="mr-1.5"
                        />

                        Histórico
                      </Button>
                    </div>
                  </Card>
                )
              )}
            </div>
          )}

          {dash.recent_ncs
            ?.length > 0 && (
            <Card className="p-5 border-border bg-card">
              <h3 className="font-heading font-semibold text-sm mb-3">
                Não conformidades recentes
              </h3>

              <div className="space-y-2">
                {dash.recent_ncs.map(
                  (nc, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-3 text-sm border-b border-border/50 last:border-0 pb-2 last:pb-0"
                    >
                      <p className="truncate">
                        <span className="font-medium">
                          {
                            nc.room_name
                          }
                        </span>{" "}
                        —{" "}
                        {nc.label}
                      </p>

                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          variant="outline"
                          className={
                            PRIORITY_COLORS[
                              nc.severity
                            ]
                          }
                        >
                          {
                            PRIORITY_LABELS[
                              nc.severity
                            ]
                          }
                        </Badge>

                        <span className="text-xs text-muted-foreground">
                          {formatDate(
                            nc.date
                          )}
                        </span>
                      </div>
                    </div>
                  )
                )}
              </div>
            </Card>
          )}
        </TabsContent>

        <TabsContent
          value="salas"
          className="mt-4"
        >
          <Card className="border-border bg-card">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <p className="font-heading font-semibold text-sm">
                {roomType ===
                "cabine_remota"
                  ? "Cadastro de Cabines Remotas"
                  : "Cadastro de Salas Elétricas"}{" "}
                ({allRooms.length})
              </p>

              {canManage && (
                <Button
                  size="sm"
                  onClick={() => {
                    setEditing(null);
                    setDialogOpen(true);
                  }}
                  data-testid="er-add-room-btn"
                >
                  <Plus
                    size={14}
                    className="mr-1.5"
                  />

                  {roomType ===
                  "cabine_remota"
                    ? "Nova Cabine"
                    : "Nova Sala"}
                </Button>
              )}
            </div>

            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="p-3 font-medium">
                      Ambiente
                    </th>

                    <th className="p-3 font-medium">
                      Cliente / Unidade
                    </th>

                    <th className="p-3 font-medium">
                      Risco atual
                    </th>

                    <th className="p-3 font-medium">
                      Status
                    </th>

                    {canManage && (
                      <th className="p-3 font-medium text-right">
                        Ações
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {allRooms.map(
                    (room) => (
                      <tr
                        key={room.id}
                        className="border-b border-border/50 last:border-0"
                        data-testid={`room-row-${room.id}`}
                      >
                        <td className="p-3">
                          <p className="font-medium">
                            {room.name}

                            {room.code
                              ? ` · ${room.code}`
                              : ""}
                          </p>

                          <p className="text-[10px] uppercase tracking-wider text-primary/80 mt-0.5">
                            {room.room_type ===
                            "cabine_remota"
                              ? "Cabine Remota"
                              : "Sala Elétrica"}
                          </p>

                          {room.area_name && (
                            <p className="text-xs text-muted-foreground">
                              Área:{" "}
                              {
                                room.area_name
                              }
                            </p>
                          )}
                        </td>

                        <td className="p-3 text-muted-foreground">
                          {
                            room.client_name
                          }{" "}
                          ›{" "}
                          {
                            room.unit_name
                          }
                        </td>

                        <td className="p-3">
                          <Badge
                            variant="outline"
                            className={
                              RISK_COLORS[
                                room.risk_level
                              ]
                            }
                          >
                            {
                              RISK_LABELS[
                                room.risk_level
                              ]
                            }
                          </Badge>
                        </td>

                        <td className="p-3">
                          <Badge
                            variant="outline"
                            className={
                              room.active ===
                              false
                                ? "bg-slate-500/15 text-slate-400 border-slate-500/30"
                                : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                            }
                          >
                            {room.active ===
                            false
                              ? "Inativa"
                              : "Ativa"}
                          </Badge>
                        </td>

                        {canManage && (
                          <td className="p-3">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => {
                                  setEditing(
                                    room
                                  );
                                  setDialogOpen(
                                    true
                                  );
                                }}
                                data-testid={`room-edit-btn-${room.id}`}
                              >
                                <Pencil
                                  size={14}
                                />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() =>
                                  toggleRoom(
                                    room
                                  )
                                }
                                data-testid={`room-toggle-btn-${room.id}`}
                                title={
                                  room.active ===
                                  false
                                    ? "Ativar"
                                    : "Desativar"
                                }
                              >
                                <Power
                                  size={14}
                                />
                              </Button>

                              {user.role ===
                                "admin" && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-destructive"
                                  onClick={() =>
                                    deleteRoom(
                                      room
                                    )
                                  }
                                  data-testid={`room-delete-btn-${room.id}`}
                                >
                                  <Trash2
                                    size={14}
                                  />
                                </Button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  )}

                  {allRooms.length ===
                    0 && (
                    <tr>
                      <td
                        colSpan={
                          canManage
                            ? 5
                            : 4
                        }
                        className="p-6 text-center text-muted-foreground text-sm"
                      >
                        Nenhum ambiente
                        cadastrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent
          value="historico"
          className="mt-4"
        >
          <Card className="border-border bg-card">
            <div className="p-4 border-b border-border flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-heading font-semibold text-sm">
                  Histórico de monitoramentos (
                  {inspections.length})
                </p>

                <p className="text-xs text-muted-foreground mt-1">
                  {typeLabel} ·{" "}
                  {period === "todos"
                    ? "todo o histórico"
                    : period === "personalizado"
                    ? `período ${
                        startDate || "início"
                      } a ${
                        endDate || "hoje"
                      }`
                    : period === "hoje"
                    ? "hoje"
                    : period === "7d"
                    ? "últimos 7 dias"
                    : period === "90d"
                    ? "últimos 90 dias"
                    : period === "30d"
                    ? "últimos 30 dias"
                    : period ===
                      "semana"
                    ? `semana ${
                        filterWeek ||
                        "atual"
                      }`
                    : period === "mes"
                    ? `mês ${filterMonth}/${filterYear}`
                    : `ano ${filterYear}`}
                </p>
              </div>

              {canInspect && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={
                    downloadReport
                  }
                  data-testid="er-history-pdf-btn"
                >
                  <FileDown
                    size={14}
                    className="mr-1.5"
                  />

                  Gerar PDF
                </Button>
              )}
            </div>

            <div className="divide-y divide-border/50">
              {inspections.map(
                (inspection) => (
                  <div
                    key={inspection.id}
                    className="flex items-center gap-2 hover:bg-accent transition-colors"
                    data-testid={`inspection-row-${inspection.id}`}
                  >
                    <button
                      onClick={() =>
                        setDetailId(
                          inspection.id
                        )
                      }
                      className="flex-1 text-left p-4 flex items-center justify-between gap-3 min-w-0"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {
                            inspection.room_name
                          }
                        </p>

                        <p className="text-[9px] uppercase tracking-wider text-primary/70 mt-0.5">
                          {inspection.room_type ===
                          "cabine_remota"
                            ? "Cabine Remota"
                            : "Sala Elétrica"}
                        </p>

                        <p className="text-xs text-muted-foreground">
                          {
                            inspection.inspector_name
                          }{" "}
                          ·{" "}
                          {formatDate(
                            inspection.finished_at
                          )}

                          {inspection
                            .occurrence_protocols
                            ?.length
                            ? ` · ${inspection.occurrence_protocols.length} ocorrência(s)`
                            : ""}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline">
                          {
                            inspection.nc_count
                          }{" "}
                          NC
                        </Badge>

                        <Badge
                          variant="outline"
                          className={
                            RISK_COLORS[
                              inspection.risk_level
                            ]
                          }
                        >
                          {
                            RISK_LABELS[
                              inspection.risk_level
                            ]
                          }
                        </Badge>
                      </div>
                    </button>

                    <div className="pr-3">
                      <RecordActions
                        module="inspecoes_salas"
                        testId={
                          inspection.id
                        }
                        onView={() =>
                          setDetailId(
                            inspection.id
                          )
                        }
                        onEdit={() =>
                          setInspEdit(
                            inspection
                          )
                        }
                        onReview={() =>
                          setInspReview(
                            inspection
                          )
                        }
                        onDelete={() =>
                          setInspDelete(
                            inspection
                          )
                        }
                      />
                    </div>
                  </div>
                )
              )}

              {inspections.length ===
                0 && (
                <p className="p-6 text-center text-muted-foreground text-sm">
                  Nenhum monitoramento
                  encontrado para os
                  filtros selecionados.
                </p>
              )}
            </div>

            {historyPages > 1 && (
              <div
                className="flex items-center justify-between gap-3 border-t border-border p-3 flex-wrap"
                data-testid="er-history-pagination"
              >
                <p className="text-xs text-muted-foreground">
                  {historyTotal} registro(s) ·{" "}
                  página {historyPage} de{" "}
                  {historyPages}
                </p>

                <div className="flex items-center gap-1.5">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      historyPage <= 1
                    }
                    onClick={() =>
                      setHistoryPage(
                        (current) =>
                          Math.max(
                            1,
                            current - 1
                          )
                      )
                    }
                    data-testid="er-history-prev-page"
                  >
                    Anterior
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      historyPage >=
                      historyPages
                    }
                    onClick={() =>
                      setHistoryPage(
                        (current) =>
                          Math.min(
                            historyPages,
                            current + 1
                          )
                      )
                    }
                    data-testid="er-history-next-page"
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <RoomDialog
        open={dialogOpen}
        onOpenChange={
          setDialogOpen
        }
        initial={editing}
        clients={clients}
        onSaved={loadAll}
      />

      <InspectionDetailDialog
        inspId={detailId}
        onClose={() =>
          setDetailId(null)
        }
      />

      <RoomHistoryDialog
        roomId={historyRoomId}
        onClose={() =>
          setHistoryRoomId(null)
        }
      />

      <AiAnalysisDialog
        open={aiOpen}
        onOpenChange={setAiOpen}
        clientId={clientId}
        unitId={unitId}
      />

      <MonitoringEditDialog
        open={!!inspEdit}
        onOpenChange={(value) =>
          !value &&
          setInspEdit(null)
        }
        kind="sala"
        record={inspEdit}
        onSaved={loadAll}
      />

      <ReviewDialog
        open={!!inspReview}
        onOpenChange={(value) =>
          !value &&
          setInspReview(null)
        }
        entity="monitoramento"
        entityLabel={
          inspReview?.room_name
        }
        url={`/electric-rooms/inspections/${inspReview?.id}/review`}
        onDone={loadAll}
      />

      <ConfirmDeleteDialog
        open={!!inspDelete}
        onOpenChange={(value) =>
          !value &&
          setInspDelete(null)
        }
        entity="monitoramento"
        entityLabel={`${
          inspDelete?.room_name ||
          ""
        } — ${formatDate(
          inspDelete?.finished_at
        )}`}
        impactUrl={
          inspDelete
            ? `/electric-rooms/inspections/${inspDelete.id}/delete-impact`
            : null
        }
        deleteUrl={`/electric-rooms/inspections/${inspDelete?.id}`}
        cascadeLabel="Excluir também as ocorrências geradas por este monitoramento"
        onDeleted={loadAll}
      />

      {canRetroactive && (
        <RetroInspectionDialog
          rooms={dash?.rooms || []}
          template={template}
          open={retroOpen}
          onOpenChange={setRetroOpen}
          onSaved={loadAll}
        />
      )}

      {canChecklist && (
        <ChecklistConfigDialog
          open={checklistOpen}
          onOpenChange={setChecklistOpen}
          onSaved={loadAll}
        />
      )}
    </div>
  );
}